import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Snackbar,
  Select,
} from "@mui/material";
import API from "../../../../API/API";
import cookie from "../../../../API/cookie";

const ROLE_OPTIONS = [
  { value: 0, label: "일반 사용자" },
  { value: 2, label: "제한 관리자" },
  { value: 1, label: "슈퍼 관리자" },
];

function RoleChip({ role }) {
  const map = {
    0: { label: "일반", color: "default" },
    1: { label: "슈퍼관리자", color: "error" },
    2: { label: "제한관리자", color: "warning" },
  };
  const cfg = map[role] ?? map[0];
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

export default function GroupManagePage() {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");

  const [userSearchInput, setUserSearchInput] = useState("");
  const [userSearch, setUserSearch] = useState("");

  const [recipes, setRecipes] = useState([]);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [selectedGroupRecipeId, setSelectedGroupRecipeId] = useState("");

  const [toast, setToast] = useState({ open: false, msg: "" });

  const apiToken = cookie.getCookie("accessToken");


  const defaultValue = useMemo(() => {
    if (!apiToken) return null;
    return { key: apiToken };
  }, [apiToken]);
  
  const selectedGroup = useMemo(
    () => groups.find((g) => g.id === selectedGroupId) || null,
    [groups, selectedGroupId]
  );

  const groupNameById = useMemo(() => {
    const m = new Map();
    groups.forEach((g) => m.set(g.id, g.name));
    return m;
  }, [groups]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const a = (u.username || "").toLowerCase();
      const b = (u.email || "").toLowerCase();
      const c = (u.first_name || "").toLowerCase();
      return a.includes(q) || b.includes(q) || c.includes(q);
    });
  }, [users, userSearch]);

  useEffect(() => {
    const t = setTimeout(() => setUserSearch(userSearchInput), 150);
    return () => clearTimeout(t);
  }, [userSearchInput]);

  useEffect(() => {
  const loadRecipes = async () => {
    setRecipesLoading(true);
    try {
      const res = await API.getRecipeList(defaultValue);
      const list = res?.data || res || [];
      setRecipes(Array.isArray(list) ? list : []);
    } finally {
      setRecipesLoading(false);
    }
  };

  if (defaultValue) loadRecipes();
}, [defaultValue]);

useEffect(() => {
  if (!selectedGroup) {
    setSelectedGroupRecipeId("");
    return;
  }
  setSelectedGroupRecipeId(
    selectedGroup.stimulus_recipe_id == null ? "" : String(selectedGroup.stimulus_recipe_id)
  );
}, [selectedGroup]);

const handleSaveGroupRecipe = async (overrideRecipeId = undefined) => {
  if (!selectedGroupId) {
    alert("그룹을 먼저 선택하세요.");
    return;
  }
  if (!defaultValue) {
    alert("로그인이 필요합니다.");
    return;
  }

  const recipeId =
    overrideRecipeId !== undefined
      ? overrideRecipeId
      : (selectedGroupRecipeId === "" ? null : Number(selectedGroupRecipeId));

  const res = await API.getAPI_UserGroupUpdate(selectedGroupId, { stimulus_recipe_id: recipeId }, defaultValue);

  if (!res) {
    alert("그룹 자극 할당에 실패했습니다.");
    return;
  }

  // 그룹 목록 새로고침
  await loadAll();
  setToast({ open: true, msg: "그룹 기본 자극이 저장되었습니다." });
};

const handleClearGroupRecipe = async () => {
  setSelectedGroupRecipeId("");
  await handleSaveGroupRecipe(null);
};

const loadAll = async () => {
  if (!defaultValue) return;

  const gRes = await API.getAPI_UserGroupList(defaultValue);
  const gList = gRes?.data ?? [];
  setGroups(gList);

  let uRes;
  try {
    uRes = await API.getAPI_UserList(
      "",     // search
      null,
      null,
      null,
      1,      // pageNumber
      10000,  // count
      defaultValue
    );
  } catch (e) {
    console.error("[GroupManage] get users failed", e);
    setUsers([]);
    return;
  }

  const uList = uRes?.data ?? [];
  setUsers(uList);

  if (gList.length && selectedGroupId == null) {
    setSelectedGroupId(gList[0].id);
  }
};

  useEffect(() => {
    if(!defaultValue) return;
    loadAll();
  }, [defaultValue]);

  const handleCreateGroup = async () => {
    const name = newName.trim();
    if (!name) {
      alert("그룹명을 입력해주세요.");
      return;
    }
    await API.getAPI_UserGroupCreate(name, newDesc || "", defaultValue);
    setNewName("");
    setNewDesc("");
    await loadAll();
  };

  const handleDeleteGroup = async (groupId) => {
    const g = groups.find((x) => x.id === groupId);
    const ok = window.confirm(`"${g?.name ?? groupId}" 그룹을 삭제할까요?\n(그룹에 속한 유저는 자동으로 그룹 해제됩니다)`);
    if (!ok) return;

    await API.getAPI_UserGroupDelete(groupId, defaultValue);
    await loadAll();
    if (selectedGroupId === groupId) {
      setSelectedGroupId(null);
    }
  };

  const handleAssignUser = async (userId) => {
    if (!selectedGroupId) {
      alert("왼쪽에서 그룹을 먼저 선택해주세요.");
      return;
    }
    await API.getAPI_UserAssignGroup(userId, selectedGroupId, defaultValue);
    await loadAll();
  };

  const handleUnassignUser = async (userId) => {
    await API.getAPI_UserAssignGroup(userId, null, defaultValue);
    await loadAll();
  };

  const handleChangeRole = async (userId, newRole) => {
    const res = await API.getAPI_UserAdmin(userId, newRole, defaultValue);
    if (!res) {
      alert("권한 변경에 실패했습니다.");
      return;
    }
    setToast({ open: true, msg: "권한이 변경되었습니다." });
    await loadAll();
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        그룹 관리
      </Typography>

    <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
      {/* 1단: 그룹 목록/생성/삭제 */}
      <Card sx={{ width: { xs: "100%", md: 420 } }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            그룹 목록
          </Typography>

          <List dense sx={{ maxHeight: 360, overflow: "auto", border: "1px solid #eee", borderRadius: 1 }}>
            {groups.map((g) => (
              <ListItemButton
                key={g.id}
                selected={g.id === selectedGroupId}
                onClick={() => setSelectedGroupId(g.id)}
              >
                <ListItemText primary={g.name} secondary={g.description || ""} />
              </ListItemButton>
            ))}
            {!groups.length && (
              <Box sx={{ p: 2, color: "text.secondary" }}>
                그룹이 없습니다.
              </Box>
            )}
          </List>

          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              variant="outlined"
              color="error"
              disabled={!selectedGroupId}
              onClick={() => handleDeleteGroup(selectedGroupId)}
            >
              선택 그룹 삭제
            </Button>
            <Button variant="outlined" onClick={loadAll}>
              새로고침
            </Button>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            그룹 생성
          </Typography>

          <Stack spacing={1}>
            <TextField
              label="그룹명"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              size="small"
            />
            <TextField
              label="설명"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              size="small"
              multiline
              minRows={2}
            />
            <Button variant="contained" onClick={handleCreateGroup}>
              생성
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* 2단: 유저 -> 그룹 할당 */}
      <Card sx={{ flex: 1, minWidth: { md: 520 } }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">
              유저 그룹 할당 {selectedGroup ? `- ${selectedGroup.name}` : ""}
            </Typography>
            <Button variant="outlined" onClick={loadAll}>새로고침</Button>
          </Stack>

          <TextField
            label="유저 검색"
            value={userSearchInput}
            onChange={(e) => setUserSearchInput(e.target.value)}
            size="small"
            fullWidth
            sx={{ mb: 2 }}
          />

          <Box sx={{ maxHeight: 520, overflow: "auto", border: "1px solid #eee", borderRadius: 1 }}>
            {filteredUsers.map((u) => {
              const curGroupName = u.user_group_id
                ? (groupNameById.get(u.user_group_id) || `#${u.user_group_id}`)
                : "없음";

              const inSelected = selectedGroupId && u.user_group_id === selectedGroupId;
              const currentRole = u.is_staff ?? 0;

              return (
                <Box key={u.id} sx={{ p: 1.25, borderBottom: "1px solid #f2f2f2" }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {u.username} ({u.email})
                        </Typography>
                        <RoleChip role={currentRole} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        현재 그룹: {curGroupName}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                      {/* 권한 변경 셀렉터 */}
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>권한</InputLabel>
                        <Select
                          label="권한"
                          value={currentRole}
                          onChange={(e) => handleChangeRole(u.id, e.target.value)}
                        >
                          {ROLE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* 그룹 할당/해제 */}
                      {inSelected ? (
                        <Button size="small" color="warning" variant="outlined" onClick={() => handleUnassignUser(u.id)}>
                          그룹 해제
                        </Button>
                      ) : (
                        <Button size="small" variant="contained" onClick={() => handleAssignUser(u.id)} disabled={!selectedGroupId}>
                          선택 그룹에 할당
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              );
            })}

            {!filteredUsers.length && (
              <Box sx={{ p: 2, color: "text.secondary" }}>
                유저가 없습니다.
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* 3단: 그룹 -> 자극 할당 */}
      <Card sx={{ width: { xs: "100%", md: 420 } }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            그룹 자극 할당
          </Typography>

          {!selectedGroup ? (
            <Typography variant="body2" color="text.secondary">
              그룹을 선택하면 기본 자극 레시피를 지정할 수 있어요.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                선택된 그룹: <b>{selectedGroup.name}</b>
              </Typography>

              <FormControl fullWidth size="small" disabled={recipesLoading}>
                <InputLabel id="group-recipe-label">기본 자극 레시피</InputLabel>
                <Select
                  labelId="group-recipe-label"
                  label="기본 자극 레시피"
                  value={selectedGroupRecipeId}
                  onChange={(e) => setSelectedGroupRecipeId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>없음(미지정)</em>
                  </MenuItem>

                  {recipes.map((r) => (
                    <MenuItem key={r.id} value={String(r.id)}>
                      {r.name || "이름 없는 자극"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button variant="contained" onClick={() => handleSaveGroupRecipe()} disabled={recipesLoading}>                  
                  저장
                </Button>
                <Button variant="outlined" color="warning" onClick={handleClearGroupRecipe} disabled={recipesLoading}>
                  해제
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Stack>

    <Snackbar
      open={toast.open}
      autoHideDuration={2000}
      onClose={() => setToast({ open: false, msg: "" })}
      message={toast.msg}
    />
    </Box>
  );
}
